import React from 'react';
import { MoreVert, Send, Block, Visibility, Refresh } from '@mui/icons-material';
import { InvoiceStatusBadge } from '@components/atoms/InvoiceStatusBadge';
import { Invoice, InvoiceStatus } from '@models/invoice.model';
import { Project } from '@models/project.model';
import { formatForDisplay } from '@utils/formatDate';

interface Props {
  invoices: Invoice[];
  projects: Project[];
  isLoading?: boolean;
  onView: (invoice: Invoice) => void;
  onResend: (invoice: Invoice) => void;
  onVoid: (invoice: Invoice) => void;
  onRefreshStatus?: (invoice: Invoice) => void;
}

const fmtCurrency = (n: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(n || 0));

export const InvoicesAdminTable: React.FC<Props> = ({
  invoices,
  projects,
  isLoading,
  onView,
  onResend,
  onVoid,
  onRefreshStatus,
}) => {
  const projectNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    projects.forEach((p) => {
      if (p.projectId) map.set(p.projectId, p.name);
    });
    return map;
  }, [projects]);

  if (isLoading && invoices.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center text-sm text-gray-500">
        Loading invoices…
      </div>
    );
  }

  if (!isLoading && invoices.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
        <p className="text-sm text-gray-600">No invoices match the current filters.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
            <tr>
              <th className="px-4 py-3 text-left">Invoice</th>
              <th className="px-4 py-3 text-left">Project</th>
              <th className="px-4 py-3 text-left">Recipient</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3 text-left">Created</th>
              <th className="px-4 py-3 text-left">Sent</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {invoices.map((invoice) => {
              const isSent = invoice.status === InvoiceStatus.SENT;
              const isVoidable =
                invoice.status === InvoiceStatus.SENT || invoice.status === InvoiceStatus.PAID;
              const projectName = projectNameById.get(invoice.projectId) || '—';
              return (
                <tr key={invoice.invoiceId} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => onView(invoice)}
                      className="font-medium text-blue-800 hover:underline">
                      {invoice.invoiceNumber || invoice.invoiceId.slice(0, 8)}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{projectName}</td>
                  <td className="px-4 py-3 text-gray-700">{invoice.recipientEmail}</td>
                  <td className="px-4 py-3">
                    <InvoiceStatusBadge status={invoice.status} size="sm" />
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900">
                    {fmtCurrency(Number(invoice.total), invoice.currency)}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {invoice.createdAt ? formatForDisplay(invoice.createdAt) : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {invoice.sentAt ? formatForDisplay(invoice.sentAt) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        title="View invoice"
                        onClick={() => onView(invoice)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-600">
                        <Visibility sx={{ fontSize: 18 }} />
                      </button>
                      {isSent && (
                        <button
                          type="button"
                          title="Resend invoice email"
                          onClick={() => onResend(invoice)}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-600">
                          <Send sx={{ fontSize: 18 }} />
                        </button>
                      )}
                      {isVoidable && (
                        <button
                          type="button"
                          title="Void invoice"
                          onClick={() => onVoid(invoice)}
                          className="p-1.5 rounded hover:bg-red-50 text-red-600">
                          <Block sx={{ fontSize: 18 }} />
                        </button>
                      )}
                      {isSent && onRefreshStatus && (
                        <button
                          type="button"
                          title="Refresh status from NMI"
                          onClick={() => onRefreshStatus(invoice)}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-600">
                          <Refresh sx={{ fontSize: 18 }} />
                        </button>
                      )}
                      {!isSent && !isVoidable && (
                        <span className="p-1.5 text-gray-300">
                          <MoreVert sx={{ fontSize: 18 }} />
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
